import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

function fileIcon(name) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['jpg','jpeg','png','gif','webp','heic'].includes(ext)) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['xls','xlsx','csv'].includes(ext)) return '📊';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['zip','rar','7z'].includes(ext)) return '🗜️';
  return '📎';
}

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' o';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
  return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
}

export default function PJ() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/pj/${id}`).then(r => r.data.files || []).catch(() => []),
      api.get(`/dossier/${id}`).then(r => r.data.dossier).catch(() => null),
    ]).then(([f, d]) => {
      setFiles(f);
      setDossier(d);
    }).finally(() => setLoading(false));
  }, [id]);

  const handleOpen = async (file) => {
    if (downloading === file.name) return;
    setDownloading(file.name);
    try {
      const res = await api.get(`/pj/${id}/${encodeURIComponent(file.name)}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch {
      alert('Erreur lors de l\'ouverture du fichier');
    } finally {
      setDownloading(null);
    }
  };

  const isSAV = dossier?.is_sav;
  const isVT = dossier?.typo_raccordement === 'VT';
  const crRoute = isSAV ? `/cr-sav/${id}` : isVT ? `/cr-vt/${id}` : `/cr/${id}`;

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="back-btn" onClick={() => navigate(`/dossier/${id}`)}>‹</div>
        <div className="topbar-title">Pièces jointes</div>
        <div style={{ fontSize: 11, color: 'var(--texte-doux)' }}>
          {loading ? '' : `${files.length} doc${files.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      <div className="content">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--texte-doux)' }}>Chargement…</div>
        ) : files.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--texte-faible)' }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>📎</div>
            <div style={{ fontWeight: 600, color: 'var(--texte-doux)', fontSize: 15 }}>Aucune pièce jointe</div>
            <div style={{ fontSize: 13, marginTop: 8, color: 'var(--texte-faible)' }}>
              Les plans et documents de ce dossier<br />apparaîtront ici dès qu'ils seront ajoutés.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {files.map((f, i) => (
              <div
                key={i}
                onClick={() => handleOpen(f)}
                style={{
                  background: 'var(--surface)',
                  borderRadius: 14,
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  cursor: 'pointer',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                  opacity: downloading === f.name ? 0.6 : 1,
                  transition: 'opacity .15s',
                }}
              >
                <span style={{ fontSize: 30, flexShrink: 0 }}>{fileIcon(f.name)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 600, fontSize: 14, color: 'var(--texte)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {f.name}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--texte-doux)', marginTop: 3 }}>
                    {formatSize(f.size)} · {new Date(f.date).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <span style={{ color: 'var(--accent)', fontSize: 20, flexShrink: 0 }}>
                  {downloading === f.name ? '⏳' : '↓'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bottomnav">
        <div className="nav-item" onClick={() => navigate('/planning')}>
          <span className="nav-icon">📅</span>Planning
        </div>
        <div className="nav-item" onClick={() => navigate(`/dossier/${id}`)}>
          <span className="nav-icon">📁</span>Dossier
        </div>
        <div className="nav-item active">
          <span className="nav-icon">📎</span>PJ
        </div>
        <div className="nav-item" onClick={() => navigate(crRoute)}>
          <span className="nav-icon">📝</span>CR
        </div>
      </div>
    </div>
  );
}
